import { FC, useMemo, useEffect, useState, useCallback } from 'react'
import {
  endOfMonth,
  startOfMonth,
  format,
  differenceInCalendarDays,
  parseISO,
  getUnixTime,
  addMinutes,
  isAfter
} from 'date-fns'
import { push } from 'connected-react-router'
import { generatePath } from 'react-router'

import MainHeader from '../../components/Calendar/Header'
import CalendarFilters from '../../components/Calendar/Filters'
import Checkbox from '../../components/UI/Inputs/Checkbox'
import CustomPopup from '../../components/UI/Popup'
import CustomButton from '../../components/UI/Buttons/CustomButton'
import ConfirmPopup from '../../components/UI/ConfirmPopup'
import Preloader from '../../components/Preloader'
import ModalSendToApproval from '../../components/PostCreation/SendToApproveModal'
import ModalDuplicatePost from '../../components/Calendar/ModalDuplicatePost'
import Popup from 'reactjs-popup'

import { useDispatch, useSelector } from '../../store'
import {
  setFiltersAction,
  clearFiltersAction,
  openSchedulePostModal,
  setHeaderView
} from '../../store/application/slice'
import { setSelectedSchedulesAction } from '../../store/calendar/slice'
import { TPost, TPostSocial } from '../../store/managePost/types'
import { TDuplicatePost } from '../../store/modal/duplicatePost/types'
import {
  toggleIsOpenAprrovalModalAction,
  setPostsForApprovalAction
} from '../../store/modal/approvalPost/slice'

import SelectedPostPopup, { SELECTED_POPUP_PAGE } from '../../components/SelectedPostPopup'
import DateRangePicker from '../../components/UI/Inputs/DateRangePicker'
import { AUTH_ROUTE } from '../App/routes'

import ExampleStory from '../../assets/img/example/story.svg'
import WaitingIcon from '../../components/UI/Icons/statuses/Waiting'
import SendIcon from '../../components/UI/Icons/Send'
import DublicateIcon from '../../components/UI/Icons/Dublicate'
import EditIcon from '../../components/UI/Icons/Edit'
import TrashIcon from '../../components/UI/Icons/Trash'

import {
  ATTACHMENT_TYPE,
  POST_SOCIAL_STATUS,
  POST_STATUS,
  SOCIAL_NETWORK,
  TPostAttachment
} from '../../models/post'
import {
  getSchedulesThunk,
  softDeletePostThunk,
  TGetSchedulesData,
  batchSoftDeletePostsThunk
} from '../../store/calendar/thunks'
import { defineAvailablePostsActions } from '../../models/postStatuses'

import './Content.scss'
import { TLatestSchedule } from '../../store/managePost/newTypes'
import { convertToLocalTime } from 'date-fns-timezone'
import { isFulfilled } from '@reduxjs/toolkit'
import {
  HEADER_VIEW,
  SCHEDULE_ACTION_TYPES,
  TModalOptions,
  TypeActionToModal
} from '../../store/application/types'
import {
  setDuplicatedPostAction,
  setPostsForDuplicate
} from '../../store/modal/duplicatePost/slice'
import { OriginalSocialIcons } from '../../components/UI/Icons/socials'
import QuestionIcon from '../../components/UI/Icons/Question'
import LoadableImage, { ASC_PREVIEW_SIZE } from '../../components/UI/LoadableImage'

type TPostList = TPost & {
  checked: boolean
}

type TDisplayInformationPost = {
  postFirstAttachment: TPostAttachment | any
  postFirstMessage: string
}

const PostStatusesForDisplay: { [key in POST_STATUS]: string } = {
  [POST_STATUS.STATUS_TEMPORARY_DRAFT]: 'Temporary draft',
  [POST_STATUS.STATUS_DRAFT]: 'Draft',
  [POST_STATUS.STATUS_PENDING_APPROVAL]: 'Pending approval',
  [POST_STATUS.STATUS_REQUIRES_REVISION]: 'Requires revision',
  [POST_STATUS.STATUS_PENDING_PUBLISH]: 'Pending manual publish',
  [POST_STATUS.STATUS_SCHEDULED]: 'Scheduled',
  [POST_STATUS.STATUS_LIVE]: 'Live',
  [POST_STATUS.STATUS_DELETED]: 'Deleted',
  [POST_STATUS.STATUS_ON_PUBLISHING]: 'On publishing',
  [POST_STATUS.STATUS_PUBLISHING_FAILED]: 'Publishing failed'
}

enum FilterTypes {
  POST_TYPE = 'post',
  POST_SOCIAL_TYPE = 'post_social',
  SOCIAL_NETWORK_TYPE = 'social_network',
  SOCIAL_POST_TYPE = 'social_post_type'
}

const displayInformationPosts: { [key in number]: TDisplayInformationPost } = {
  0: {
    postFirstMessage: 'Empty description',
    postFirstAttachment: ExampleStory
  }
}

interface AppProps { }

const Content: FC<AppProps> = ({ children }) => {
  const dispatch = useDispatch()

  const [modalOptions, setModalOptions] = useState<TModalOptions | null>(null)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [filteredPosts, setFilteredPosts] = useState<TPostList[]>([])
  const [checkedPostsIds, setCheckedPostsIds] = useState<number[]>([])
  const [posts, setPosts] = useState<TPost[]>([])
  const [startEndDatesForDatePicker, setStartEndDatesForDatePicker] = useState<[Date, Date]>([
    startOfMonth(new Date()),
    endOfMonth(new Date())
  ])
  localStorage.setItem('startEndDates', JSON.stringify(startEndDatesForDatePicker))

  useEffect(() => {
    dispatch(setHeaderView(HEADER_VIEW.CONTENT))
  }, [])

  const { filters, projectId, duplicatedPost } = useSelector((state) => {
    return {
      filters: state.app.filters,
      projectId: state.project.currentProject.id,
      // TODO change it when content page will work with store data. It just for content page after the post was duplicated
      duplicatedPost: state.duplicateModal.duplicatedPost
    }
  })

  // TODO change it when content page will work with store data. It just for content page after the post was duplicated
  useEffect(() => {
    if (duplicatedPost) {
      setPosts([...posts, duplicatedPost])
      setDuplicatedPostAction(null)
    }
  }, [duplicatedPost])

  const onChangeDates = (dates: [Date, Date]) => {
    setStartEndDatesForDatePicker([...dates])
  }

  const filterProps = useMemo(() => {
    return {
      filters,
      setFilters: (props: any) => {
        dispatch(setFiltersAction(props))
      },
      clearFilters: () => {
        dispatch(clearFiltersAction())
      }
    }
  }, [dispatch, filters])

  useEffect(() => {
    if (projectId && startEndDatesForDatePicker[0] && startEndDatesForDatePicker[1]) {
      const getPosts = async () => {
        setIsLoading(true)
        setPosts([])
        const payload: TGetSchedulesData = {
          id: projectId,
          start: format(startEndDatesForDatePicker[0], 'yyyy-MM-dd'),
          end: format(startEndDatesForDatePicker[1], 'yyyy-MM-dd')
        }
        const {
          payload: { data }
        } = await dispatch(getSchedulesThunk(payload))
        const postSchedules = data.post_schedules
          .map(({ post }: { post: TPost }) => post)
          .filter(({ status }: TPost) => status !== POST_STATUS.STATUS_DELETED)
        setPosts(postSchedules)
        setIsLoading(false)
      }
      getPosts()
    }
  }, [projectId, startEndDatesForDatePicker, dispatch])

  useEffect(() => {
    const postsWithCkeckedFlag = posts
      .map<TPostList>((post) => {
        const { id } = post
        if (!displayInformationPosts[id]) {
          const { post_socials: postSocials } = post
          const postFirstAttachment: TPostSocial | undefined = postSocials.find(
            ({ attachments }) => attachments.length
          )
          let postMessage: string | null = null

          post.post_socials.forEach(({ message }) => {
            postMessage = postMessage || message
          })

          const displayInformationPost: TDisplayInformationPost = {
            postFirstMessage: postMessage ? postMessage : 'Empty message',
            postFirstAttachment: postFirstAttachment
              ? postFirstAttachment.attachments[0]
              : ExampleStory
          }
          displayInformationPosts[id] = displayInformationPost
        }

        return {
          ...post,
          checked: checkedPostsIds.some((ckeckedId) => ckeckedId === post.id)
        }
      })
      .sort(({ latest_schedule: latest_scheduleA }, { latest_schedule: latest_scheduleB }) => {
        if (latest_scheduleA && latest_scheduleB) {
          const { datetime: datetimeA } = latest_scheduleA
          const { datetime: datetimeB } = latest_scheduleB
          return getUnixTime(parseISO(datetimeA)) - getUnixTime(parseISO(datetimeB))
        } else if (latest_scheduleA) return -1
        else if (latest_scheduleB) return 1
        else return 0
      })
    setFilteredPosts(postsWithCkeckedFlag)
  }, [posts])

  //apply the filters
  useEffect(() => {
    const checkedStatuses = filters.status.filter(({ checked }: { checked: boolean }) => checked)

    const checkedSocialNetwork = filters.social_network
      .filter(({ checked }: { checked: boolean }) => checked)
      .map((filter: any) => ({ ...filter, filterType: FilterTypes.SOCIAL_NETWORK_TYPE }))

    const checkedSocialPostType = filters.social_post_type
      .filter(({ checked }: { checked: boolean }) => checked)
      .map((filter: any) => ({ ...filter, filterType: FilterTypes.SOCIAL_POST_TYPE }))
    const checkedLabels = filters.label

    const isCheckedOneOrMore =
      checkedStatuses.length +
      checkedSocialNetwork.length +
      checkedSocialPostType.length +
      checkedLabels.length
    if (isCheckedOneOrMore) {
      //post statuses
      const checkedStatusesPost = checkedStatuses
        .filter(({ id }: { id: string }) =>
          Object.values(POST_STATUS).some((value) => id === value)
        )
        .map((filter: any) => ({ ...filter, filterType: FilterTypes.POST_TYPE }))
      //post statuses
      //post social statuses
      const checkedStatusesPostSocial = checkedStatuses
        .filter(({ id }: { id: POST_STATUS | POST_SOCIAL_STATUS }) =>
          Object.values(POST_SOCIAL_STATUS).some((value) => id === value)
        )
        .map((filter: any) => ({ ...filter, filterType: FilterTypes.POST_SOCIAL_TYPE }))
      //post social statuses

      const allChekedFilters = [
        [...checkedStatusesPost, ...checkedStatusesPostSocial],
        checkedSocialNetwork,
        checkedSocialPostType
      ]

      const filteredPostsByAllCheckedFilters = posts.filter((post) => {
        return allChekedFilters.every((filtersPart) => {
          if (filtersPart.length) {
            return filtersPart.some(
              ({
                filterType,
                id,
                values
              }: {
                filterType: FilterTypes
                id: POST_SOCIAL_STATUS | POST_STATUS | SOCIAL_NETWORK
                values?: string[]
              }) => {
                const { status, post_socials: postSocials, type } = post

                switch (filterType) {
                  case FilterTypes.POST_TYPE:
                    return status === id

                  case FilterTypes.POST_SOCIAL_TYPE:
                    return postSocials.some(({ status: socialStatus }) => socialStatus === id)

                  case FilterTypes.SOCIAL_NETWORK_TYPE:
                    return postSocials.some(
                      ({ social_network: socialNetwork }) => socialNetwork === id
                    )

                  case FilterTypes.SOCIAL_POST_TYPE:
                    return values?.some((postTypeValue) => postTypeValue === type)
                }
              }
            )
          } else {
            return true
          }
        })
      })

      const postsWithCkeckedFlag = filteredPostsByAllCheckedFilters
        .map<TPostList>((post) => ({
          ...post,
          checked: checkedPostsIds.some((ckeckedId) => ckeckedId === post.id)
        }))
        .sort(({ latest_schedule: latest_scheduleA }, { latest_schedule: latest_scheduleB }) => {
          if (latest_scheduleA && latest_scheduleB) {
            const { datetime: datetimeA } = latest_scheduleA
            const { datetime: datetimeB } = latest_scheduleB
            return getUnixTime(parseISO(datetimeA)) - getUnixTime(parseISO(datetimeB))
          } else if (latest_scheduleA) return -1
          else if (latest_scheduleB) return 1
          else return 0
        })
      setFilteredPosts(postsWithCkeckedFlag)
    } else {
      const postsWithCkeckedFlag = posts
        .map<TPostList>((post) => ({
          ...post,
          checked: checkedPostsIds.some((ckeckedId) => ckeckedId === post.id)
        }))
        .sort(({ latest_schedule: latest_scheduleA }, { latest_schedule: latest_scheduleB }) => {
          if (latest_scheduleA && latest_scheduleB) {
            const { datetime: datetimeA } = latest_scheduleA
            const { datetime: datetimeB } = latest_scheduleB
            return getUnixTime(parseISO(datetimeA)) - getUnixTime(parseISO(datetimeB))
          } else if (latest_scheduleA) return -1
          else if (latest_scheduleB) return 1
          else return 0
        })
      setFilteredPosts(postsWithCkeckedFlag)
    }
  }, [filters, posts])
  //apply the filters

  useEffect(() => {
    setCheckedPostsIds((prev) =>
      prev.filter((id) => filteredPosts.some(({ id: postId }) => postId === id))
    )
  }, [filteredPosts])

  const checkUnckeckPost = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    event.preventDefault()
    const cheked: boolean = filteredPosts[index].checked
    const postId: number = filteredPosts[index].id
    if (cheked) {
      const postIndexInChekecIds = checkedPostsIds.findIndex((id) => +postId === id)
      checkedPostsIds.splice(postIndexInChekecIds, 1)
      setCheckedPostsIds([...checkedPostsIds])
    } else {
      setCheckedPostsIds((prev) => [...prev, +postId])
    }
    filteredPosts[index] = { ...filteredPosts[index], checked: !filteredPosts[index].checked }
    setFilteredPosts([...filteredPosts])
  }

  const clearModalOptions = () => {
    setIsModalOpen(false)
    setModalOptions(null)
  }

  const moveToPostEdit = (post: TPostList) => {
    const social: SOCIAL_NETWORK = post.post_socials[0].social_network
    dispatch(
      push(
        generatePath(AUTH_ROUTE.MANAGE_POST, {
          projectId: post.project_id,
          social
        }) + `?postId=${post.id}`
      )
    )
  }

  const removePosts = () => {
    setModalOptions({
      postId: 0,
      type: TypeActionToModal.DELETE_MULTI_POSTS,
      postsIds: checkedPostsIds
    })
    setIsModalOpen(true)
  }

  const modalProps = useMemo(() => {
    if (modalOptions) {
      const { type } = modalOptions
      switch (type) {
        case TypeActionToModal.DELETE_MULTI_POSTS:
          return {
            onConfirm: async () => {
              const { postsIds } = modalOptions
              const responseDeletePosts = await dispatch(batchSoftDeletePostsThunk(postsIds))
              if (isFulfilled(responseDeletePosts)) {
                setPosts(posts.filter((post) => !postsIds.includes(post.id)))
              }
              clearModalOptions()
            },
            onClose: clearModalOptions
          }
        case TypeActionToModal.DELETE_ONE_POST:
          return {
            onConfirm: async () => {
              const { postId } = modalOptions
              const responseDeletePost = await dispatch(softDeletePostThunk(postId))
              if (isFulfilled(responseDeletePost)) {
                setPosts(posts.filter((post) => post.id !== postId))
              }
              clearModalOptions()
            },
            onClose: clearModalOptions
          }
      }
    }
  }, [modalOptions])

  const uncheckAllPosts = () => {
    setFilteredPosts((prevState) => [
      ...filteredPosts.map((post) => (post.checked ? { ...post, checked: false } : post))
    ])
    setCheckedPostsIds([])
  }

  const toggleUpdateSchedules = () => {
    dispatch(
      setSelectedSchedulesAction(
        checkedPostsIds
          .map((id) => {
            const post = posts.find((post: any) => id === post.id)
            return { id: post?.latest_schedule?.id, post: post }
          })
          .filter((id) => !!id)
      )
    )
    dispatch(openSchedulePostModal(SCHEDULE_ACTION_TYPES.BATCH_SCHEDULES))
  }

  const sheduleUpdateAction = (postIndex: number) => {
    dispatch(
      setSelectedSchedulesAction([
        { id: filteredPosts[postIndex].latest_schedule.id, post: filteredPosts[postIndex] }
      ])
    )
    dispatch(openSchedulePostModal(SCHEDULE_ACTION_TYPES.SINGLE_SCHEDULE))
  }

  // TODO need to unify posts for calendar and content
  const transforPostToApproval = useCallback(
    ({ id, latest_schedule: { date, time }, post_socials: postSocials }: TPostList) => {
      return {
        id,
        postSocials,
        schedule: {
          date,
          time
        }
      } as any
    },
    []
  )

  const filteredPostsForApproval = useMemo(
    () =>
      filteredPosts.filter(
        ({ id, status, latest_schedule: schedule }) =>
          checkedPostsIds.some((checkedId) => checkedId === id) &&
          defineAvailablePostsActions.sendToApprove(status, schedule)
      ),
    [filteredPosts]
  )

  const filteredPostsForSchedule = useMemo(
    () =>
      filteredPosts.filter(
        ({ id, status }) =>
          checkedPostsIds.some((checkedId) => checkedId === id) &&
          defineAvailablePostsActions.schedule(status)
      ),
    [filteredPosts]
  )

  const toggleSendToApprovalPost = (index: number) => {
    const post = filteredPosts[index]
    dispatch(setPostsForApprovalAction([transforPostToApproval(post)]))
    dispatch(toggleIsOpenAprrovalModalAction())
  }

  const toggleSendToApprovalPosts = async () => {
    const postsForApproval: any[] = filteredPostsForApproval.map((post) =>
      transforPostToApproval(post)
    )
    dispatch(setPostsForApprovalAction(postsForApproval))
    dispatch(toggleIsOpenAprrovalModalAction())
  }

  const toggleDuplicatePostSocial = (postIndex: number) => {
    const postForDuplicate: TDuplicatePost = {
      post_social_id: filteredPosts[postIndex].post_socials[0].id,
      social_networks: []
    }
    dispatch(setPostsForDuplicate(postForDuplicate))
  }

  const toggleDuplicatePost = () => {
    if (checkedPostsIds.length > 0) {
      const postForDuplicate: TDuplicatePost = {
        post_id: checkedPostsIds[0],
        social_networks: []
      }
      dispatch(setPostsForDuplicate(postForDuplicate))
    }
  }

  const checkIsPosibleToChange = (latestSchedule: TLatestSchedule) => {
    const { datetime, timezone, is_autoposting: isAutoposting } = latestSchedule
    if (timezone && datetime && isAutoposting) {
      const localTimeSchedule = convertToLocalTime(new Date(datetime), { timeZone: timezone })
      const availableDateForPosting = addMinutes(new Date(), 4)
      return isAfter(availableDateForPosting, localTimeSchedule)
    }
    return false
  }

  const getPostSocialFailedDetails = (postSocials: TPostSocial[]): string => {
    const details = postSocials.reduce((acc: { [key in SOCIAL_NETWORK]?: any }, postSocial) => {
      const { social_network: socialNetwork, last_social_api_response: lastSocialApiResponse } =
        postSocial
      if (lastSocialApiResponse) {
        acc[socialNetwork] = lastSocialApiResponse
      } else {
        acc[socialNetwork] = 'An internal error has occurred.'
      }
      return acc
    }, {})

    return JSON.stringify(details)
  }

  return (
    <div className='content-page-wrapper'>
      <MainHeader />
      <div className='wrapper-content-with-filter'>
        <div className='content-wrapper'>
          <div className='content-wrapper__list_datepicker'>
            <DateRangePicker
              dates={startEndDatesForDatePicker}
              datePickerProps={{ onChange: onChangeDates }}
            />
          </div>
          {isLoading ? (
            <Preloader />
          ) : (
            <>
              {filteredPosts.length ? (
                <>
                  <div className='list_labels'>
                    <div>Content information</div>
                    <div>Status</div>
                    <div>Publish date</div>
                    <div>Last update</div>
                  </div>
                  <div className='list_items'>
                    {filteredPosts.map(
                      (
                        {
                          checked,
                          id,
                          updated_at: updatedAt,
                          status,
                          project_id,
                          post_socials,
                          latest_schedule: latestSchedule
                        },
                        index
                      ) => (
                        <div
                          className={`list_post ${checked && 'list_post_cheked'}`}
                          key={id}
                          onClick={(event) => checkUnckeckPost(event, index)}
                        >
                          <div
                            className={`list_post-left-border list_post-left-border_${status}`}
                          ></div>
                          <div className='list_post-checkbox'>
                            <Checkbox
                              checked={checked}
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                checkUnckeckPost(event, index)
                              }
                            />
                          </div>
                          <div className='list_post-image'>
                            {typeof displayInformationPosts[id].postFirstAttachment === 'string' ? (
                              <LoadableImage
                                media={displayInformationPosts[id].postFirstAttachment as string}
                              />
                            ) : displayInformationPosts[id].postFirstAttachment.object_type ===
                              ATTACHMENT_TYPE.TYPE_VIDEO ? (
                              <video
                                key={`video-attachment-${displayInformationPosts[id].postFirstAttachment.id}`}
                                className='post-info__data__media-wrapper__video'
                                controls
                              >
                                <source
                                  src={
                                    displayInformationPosts[id].postFirstAttachment.object_data
                                      .public_url
                                  }
                                />
                              </video>
                            ) : (
                              <LoadableImage
                                media={displayInformationPosts[id].postFirstAttachment}
                                attachmentSize={ASC_PREVIEW_SIZE.MEDIUM}
                              />
                            )}
                          </div>
                          <div className='list_post-description'>
                            <div className='list_post-description-socials'>
                              {post_socials.map(
                                ({ social_network: socialNetwork }) =>
                                  OriginalSocialIcons[socialNetwork]
                              )}
                            </div>
                            <div className='list_post-description-text'>
                              {displayInformationPosts[id]
                                ? displayInformationPosts[id].postFirstMessage
                                : displayInformationPosts[0].postFirstMessage}
                            </div>
                          </div>
                          <div
                            className={`list_post-status-container list_post-status-container_${status}`}
                          >
                            <div className='circle'></div>
                            <div className='status'>{PostStatusesForDisplay[status]}</div>
                            {status === POST_STATUS.STATUS_PUBLISHING_FAILED && (
                              <Popup
                                className='failed-message'
                                on='hover'
                                position='left center'
                                arrow={false}
                                trigger={() => (
                                  <div>
                                    <QuestionIcon />
                                  </div>
                                )}
                              >
                                <div className='failed-message'>
                                  {getPostSocialFailedDetails(post_socials)}
                                </div>
                              </Popup>
                            )}
                          </div>
                          <div className='list_post-publish-date'>
                            {latestSchedule
                              ? format(
                                new Date(`${latestSchedule.date}T${latestSchedule.time}`),
                                'd LLL yyyy'
                              )
                              : `schedule is absent`}
                            <br />
                            {latestSchedule
                              ? format(
                                new Date(`${latestSchedule.date}T${latestSchedule.time}`),
                                'h:mm aaa'
                              )
                              : `schedule is absent`}
                          </div>
                          <div className='list_post-last-update'>
                            {differenceInCalendarDays(new Date(), parseISO(updatedAt))
                              ? `${differenceInCalendarDays(
                                new Date(),
                                parseISO(updatedAt)
                              )} days ago`
                              : 'Today'}
                          </div>
                          <div className='list_post-dropdown'>
                            <CustomPopup
                              isOpen={true}
                              className='list_post-dropdown-actions'
                              position={['bottom center', 'top center']}
                              keepTooltipInside='body'
                              trigger={() => (
                                <div className='list_post-dropdown-dots'>
                                  <div className='list_post-dropdown-dot'></div>
                                  <div className='list_post-dropdown-dot'></div>
                                  <div className='list_post-dropdown-dot'></div>
                                </div>
                              )}
                            >
                              <div className='list_post-dropdown-actions'>
                                {defineAvailablePostsActions.sendToApprove(
                                  status,
                                  latestSchedule
                                ) && (
                                    <>
                                      <CustomButton
                                        onClick={() => toggleSendToApprovalPost(index)}
                                        button='main'
                                        className='list_post-dropdown-action'
                                      >
                                        <SendIcon />
                                        To approval
                                      </CustomButton>
                                      <div className='user-actions__buttons__divide'></div>
                                    </>
                                  )}
                                {defineAvailablePostsActions.schedule(status) && (
                                  <>
                                    <CustomButton
                                      onClick={() => sheduleUpdateAction(index)}
                                      button='main'
                                      className='list_post-dropdown-action'
                                      disabled={checkIsPosibleToChange(
                                        filteredPosts[index].latest_schedule
                                      )}
                                    >
                                      <WaitingIcon />
                                      Change Schedule
                                    </CustomButton>
                                    <div className='user-actions__buttons__divide'></div>
                                  </>
                                )}
                                <CustomButton
                                  onClick={() => toggleDuplicatePostSocial(index)}
                                  button='main'
                                  className='list_post-dropdown-action'
                                >
                                  <DublicateIcon />
                                  Duplicate media to other platforms
                                </CustomButton>
                                <div className='user-actions__buttons__divide'></div>
                                <CustomButton
                                  button='main'
                                  className='list_post-dropdown-action'
                                  onClick={() => moveToPostEdit(filteredPosts[index])}
                                >
                                  <EditIcon />
                                  Edit
                                </CustomButton>
                                <div className='user-actions__buttons__divide'></div>
                                <CustomButton
                                  onClick={() => {
                                    setModalOptions({
                                      postId: +id,
                                      type: TypeActionToModal.DELETE_ONE_POST,
                                      postsIds: []
                                    })
                                    setIsModalOpen(true)
                                  }}
                                  button='main'
                                  className='list_post-dropdown-action list_post-dropdown-action_delete'
                                >
                                  <TrashIcon />
                                  Delete
                                </CustomButton>
                              </div>
                            </CustomPopup>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </>
              ) : (
                <div className='content-wrapper_empty-result'></div>
              )}
            </>
          )}
          <SelectedPostPopup
            {...{
              selectedPostIds: checkedPostsIds,
              toggleSendToApprovalPosts,
              toggleUpdateSchedules,
              toggleDuplicatePost,
              page: SELECTED_POPUP_PAGE.CONTENT,
              showDeleteModal: removePosts,
              setSelectedPosts: uncheckAllPosts,
              qtyToApprove: filteredPostsForApproval.length,
              qtyToSchedule: filteredPostsForSchedule.length
            }}
          />
        </div>
        <div className='filter-items-wrapper'>
          <CalendarFilters {...filterProps} />
        </div>
        <ConfirmPopup {...modalProps} isModalOpen={isModalOpen}>
          Are you sure you want to delete the content?
        </ConfirmPopup>
        <ModalSendToApproval resetSelectedPosts={() => setCheckedPostsIds([])} />
        <ModalDuplicatePost />
      </div>
    </div>
  )
}

export default Content
